import React, { useContext, useEffect, useMemo } from 'react';
import { useApi } from '@repo-level/core-front';
import { Slider } from 'components/Slider';
import {
  registrationBanner as fetchRegistrationBanner,
  RegistrationBanner,
} from 'api/cms/registrationBanner';
import { AddonsContext } from 'contexts/addons';
import { useTranslation } from 'react-i18next';
import { ScVipBenefits } from './styled';
import benefits1 from 'assets/images/registrationVipBenefits/benefits_1.webp';
import benefits2 from 'assets/images/registrationVipBenefits/benefits_2.webp';
import benefits3 from 'assets/images/registrationVipBenefits/benefits_3.webp';
import { Spinner } from 'components/Spinner';

export const VipBenefitsRegistrationBanner = () => {
  const {
    i18n: { language },
  } = useTranslation();

  const [
    requestRegistrationBanner,
    { data: registrationBanner, pending: pendingRegistrationBanner },
  ] = useApi(fetchRegistrationBanner);

  useEffect(() => {
    requestRegistrationBanner(language);
  }, [language, requestRegistrationBanner]);

  const slides = useMemo(
    () => [
      {
        content: registrationBanner && (
          <VipBenefitDynamic {...registrationBanner} />
        ),
      },
      {
        content: <VipBenefitStatic />,
      },
    ],
    [registrationBanner]
  );

  if (pendingRegistrationBanner) {
    return <Spinner size={32} />;
  }

  return (
    <ScVipBenefits className={language} slidesNumber={slides.length}>
      <Slider
        key={'slider'}
        slides={slides}
        pending={pendingRegistrationBanner && language}
        pagination={{ clickable: true }}
        autoplay={{ delay: 3000, disableOnInteraction: false }}
        loop
        slide={({ content }) => <>{content}</>}
        slidesPerView={1}
        slidesPerGroup={1}
        speed={3000}
      />
    </ScVipBenefits>
  );
};

const VipBenefitDynamic = (registrationBanner: RegistrationBanner) => {
  const { customOffer } = useContext(AddonsContext);
  const customBonusOffer = useMemo(
    () => (customOffer?.bonuses ? customOffer.bonuses[0].offer : null),
    [customOffer]
  );

  return (
    <div className="offers">
      <div className="title-dynamic">{registrationBanner?.title}</div>
      <div className="offer">
        {customBonusOffer ? (
          <p>{customBonusOffer}</p>
        ) : (
          <>
            <p>{registrationBanner?.offerLine_1}</p>
            {registrationBanner?.offerLine_2 && (
              <p>{registrationBanner.offerLine_2}</p>
            )}
          </>
        )}
      </div>
    </div>
  );
};

const VipBenefitStatic = () => {
  const { t } = useTranslation('common');

  const benefitsItems = [
    {
      icon: benefits1,
      description: t('auth.registration.vipBenefits.description1'),
    },
    {
      icon: benefits2,
      description: t('auth.registration.vipBenefits.description2'),
    },
    {
      icon: benefits3,
      description: t('auth.registration.vipBenefits.description3'),
    },
  ];

  return (
    <>
      <div className="title">
        {t('common:auth.registration.vipBenefits.title')}
      </div>
      <div className="wrapper">
        {benefitsItems.map((item, index) => (
          <div className="items" key={index}>
            <img className="img" src={item.icon} alt={`benefit-${index}`} />
            <div className="description">{item.description}</div>
          </div>
        ))}
      </div>
    </>
  );
};
